import { useNavigate, Link } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';
import {
  useMatchesStore,
  MATCHES_CAP,
  type MatchMeta,
  type MatchSnapshot,
} from '../../stores/matches';
import { allHeroes } from '../../game/data/heroes';
import { EDGES } from '../../theme/tokens';

// Matches hub — Phase 1 of the multi-match roadmap.
//
// Lists every in-flight match (and recent completions) the player has
// going, sorted by last activity. Each card resumes the match by
// navigating to the saved phase's URL — the route-sync hook in
// RouteLayout swaps useMatchStore over to that snapshot.
//
// Completed matches stay listed until the player drops them — provides
// a lightweight "match history" without a separate route.

const PHASE_ROUTE: Record<string, string> = {
  'hero-select': 'hero',
  draft: 'draft',
  tavern: 'tavern',
  combat: 'combat',
  results: 'results',
};

function resumeUrl(matchId: string, phase: string): string {
  const segment = PHASE_ROUTE[phase] ?? 'tavern';
  return `/game/${matchId}/${segment}`;
}

function formatAgo(ms: number): string {
  const delta = Math.max(0, Date.now() - ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (delta < minute) return 'just now';
  if (delta < hour) return `${Math.floor(delta / minute)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  return `${Math.floor(delta / day)}d ago`;
}

export function MatchesPage() {
  const navigate = useNavigate();
  const meta = useMatchesStore((s) => s.meta);
  const matches = useMatchesStore((s) => s.matches);
  const remove = useMatchesStore((s) => s.remove);

  // Sort: in-progress first, then by last activity desc.
  const ordered = Object.values(meta).sort((a, b) => {
    if (a.status === 'in-progress' && b.status !== 'in-progress') return -1;
    if (a.status !== 'in-progress' && b.status === 'in-progress') return 1;
    return b.updatedAt - a.updatedAt;
  });

  const openCount = ordered.filter((m) => m.status === 'in-progress').length;

  function handleResume(m: MatchMeta) {
    const snapshot = matches[m.matchId];
    if (!snapshot) return;
    navigate(resumeUrl(m.matchId, snapshot.phase));
  }

  function handleDrop(matchId: string) {
    if (!confirm('Drop this match? Cannot be undone.')) return;
    remove(matchId);
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-1/4 -top-1/4 h-[60vw] w-[60vw] max-h-[760px] max-w-[760px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.14) 0%, rgba(255,138,61,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-md overflow-y-auto px-md py-sm md:gap-lg md:px-lg md:py-md lg:max-w-5xl lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to="/menu"
            className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        <div className="flex items-baseline justify-between gap-md">
          <div>
            <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
              /matches
            </p>
            <h1 className="mt-xs font-cholo text-[28px] leading-none text-sodium md:text-[48px] lg:text-[72px]">
              Your runs
            </h1>
          </div>
          <span className="font-data text-[13px] uppercase tracking-[0.2em] text-graphite md:text-[13px]">
            {openCount}/{MATCHES_CAP} active
          </span>
        </div>

        {ordered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-sm md:grid-cols-2 md:gap-md xl:grid-cols-3">
            {ordered.map((m) => (
              <MatchCard
                key={m.matchId}
                meta={m}
                snapshot={matches[m.matchId]}
                onResume={() => handleResume(m)}
                onDrop={() => handleDrop(m.matchId)}
              />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function EmptyState() {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-md rounded-md border border-dashed bg-stucco1/40 px-lg py-2xl text-center"
      style={{ borderColor: EDGES.soft }}
    >
      <p className="font-data text-[13px] uppercase tracking-[0.3em] text-marker md:text-[13px]">
        nothing in flight
      </p>
      <h2 className="font-cholo text-[28px] leading-none text-bone md:text-[40px]">
        Empty lot
      </h2>
      <p className="max-w-md text-[13px] text-marker md:text-[15px]">
        You've got no runs going. Start one and the cards land here while you
        wait for your opponent — up to {MATCHES_CAP} at a time.
      </p>
      <Link
        to="/play"
        className="mt-xs inline-block min-h-[44px] rounded-sm bg-sodium px-lg py-sm font-data text-[12px] uppercase tracking-[0.2em] text-sharpie shadow-sodium hover:bg-sodiumGlow md:min-h-[52px] md:px-xl md:text-[14px]"
      >
        Start a run →
      </Link>
    </div>
  );
}

interface MatchCardProps {
  meta: MatchMeta;
  snapshot: MatchSnapshot | undefined;
  onResume: () => void;
  onDrop: () => void;
}

function MatchCard({ meta, snapshot, onResume, onDrop }: MatchCardProps) {
  const hero = meta.heroId ? allHeroes.find((h) => h.id === meta.heroId) : null;
  const factionClass =
    hero?.faction === 'emo' ? 'text-emo font-emo' : 'text-cholo font-cholo';
  const phase = snapshot?.phase ?? 'idle';

  // Status badge — Phase 1 doesn't have true turn-based state yet, so
  // the framing is just "in-progress / completed / forfeit". When
  // Phase 3 lands we'll layer in "your turn" / "waiting on Lupita".
  const statusBadge =
    meta.status === 'completed'
      ? meta.placement
        ? `Final · ${ordinal(meta.placement)} of 8`
        : 'Final'
      : meta.status === 'forfeit'
        ? 'Forfeited'
        : `Round ${meta.roundNumber} · ${phaseLabel(phase)}`;
  const statusTone =
    meta.status === 'completed'
      ? meta.placement === 1
        ? 'text-glowStick'
        : 'text-marker'
      : meta.status === 'forfeit'
        ? 'text-graphite'
        : 'text-sodium';

  const disabled = meta.status !== 'in-progress' || !snapshot;

  return (
    <motion.div
      whileHover={disabled ? undefined : { scale: 1.02 }}
      className="flex flex-col gap-sm rounded-md border bg-stucco1 p-md md:p-lg"
      style={{ borderColor: EDGES.soft }}
    >
      <div className="flex items-baseline justify-between gap-sm">
        <p className={`font-data text-[12px] uppercase tracking-[0.2em] ${statusTone}`}>
          {statusBadge}
        </p>
        <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite">
          {formatAgo(meta.updatedAt)}
        </p>
      </div>

      <h3
        className={`truncate text-[22px] leading-none md:text-[28px] ${factionClass}`}
        title={hero?.name ?? 'No hero'}
      >
        {hero?.name ?? '— pick a hero —'}
      </h3>

      <div className="flex items-baseline gap-md font-data text-[13px] uppercase tracking-[0.2em] text-marker md:text-[12px]">
        <span>
          HP <span className="text-aerosolRed">{meta.health}</span>
        </span>
        {meta.lastAsyncOpponentHandle ? (
          <span className="truncate" title={`vs ${meta.lastAsyncOpponentHandle}`}>
            vs{' '}
            <span className="text-bone">{meta.lastAsyncOpponentHandle}</span>
          </span>
        ) : (
          <span className="text-graphite">vs AI lobby</span>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-sm pt-sm">
        <button
          onClick={onDrop}
          className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-aerosolRed"
        >
          drop
        </button>
        <button
          onClick={onResume}
          disabled={disabled}
          className={`rounded-sm px-md py-sm font-data text-[13px] uppercase tracking-[0.2em] md:px-lg md:text-[13px] ${
            disabled
              ? 'cursor-not-allowed bg-stucco2 text-graphite'
              : 'bg-sodium text-sharpie hover:bg-sodiumGlow'
          }`}
        >
          {disabled ? 'closed' : 'resume →'}
        </button>
      </div>
    </motion.div>
  );
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

function phaseLabel(phase: string): string {
  switch (phase) {
    case 'hero-select':
      return 'pick hero';
    case 'draft':
      return 'starter draft';
    case 'tavern':
      return 'tavern';
    case 'combat':
      return 'combat';
    case 'results':
      return 'results';
    default:
      return phase;
  }
}
